/**
 * What the public site says and how it looks — the Home and Features copy,
 * the look, and the screenshot library — as Platform › Site content edits it
 * (design_handoff_site_content; server migration 078).
 *
 * The site reads the PUBLISHED document from `/api/site/content`. The
 * constants below are two things at once: the seed 078 wrote into the
 * database (`siteContentSeed.test` pins them to it, word for word), and what
 * the pages draw when that read fails — so an outage shows the site as it
 * was launched, never an empty page.
 *
 * The server keeps its own copy of the shape (server/src/lib/siteDoc.ts);
 * the two are the same document and change together.
 */

export type SiteAccent = 'indigo' | 'green' | 'orange' | 'ink';
export type SiteFont = 'sans' | 'serif';
export type SiteRadius = 'round' | 'square';

export interface SiteLook {
  accent: SiteAccent;
  /** Headings only (h1, h2 and the pillar titles); body text stays Geist. */
  font: SiteFont;
  radius: SiteRadius;
  /** An uploaded icon's file name (served at /favicon.ico), or null for the built-in FM mark. */
  favicon: string | null;
}

export interface SitePillar { k: string; title: string; body: string }

export interface SiteHome {
  /** The announcement pill above the headline; empty hides it. */
  eyebrow: string;
  /** Where the pill goes — a path on the site, "/community/releases". */
  eyebrowLink: string;
  h1: string;
  lede: string;
  /** The two buttons' words. Both go to /pricing, always (signupStartsAtPricing.test). */
  cta1: string;
  cta2: string;
  /** The hero's screenshot, a shot id. */
  hero: string;
  /** Exactly three. */
  pillars: SitePillar[];
}

export interface SiteFeatureRow {
  /** Fixed: the anchor (/features#access is linked from the FAQ). */
  id: string;
  /** The index's word for it. */
  short: string;
  title: string;
  lede: string;
  points: string[];
  /** A shot id. */
  shot: string;
}

export interface SiteFeatures {
  h1: string;
  sub: string;
  /** The nine rows, in FEATURE_ROW_IDS order — neither the order nor the ids are editable. */
  rows: SiteFeatureRow[];
}

export interface SiteDoc {
  look: SiteLook;
  home: SiteHome;
  features: SiteFeatures;
}

/** One image of a shot: a capture shipped in client/public/marketing, or an upload. */
export interface SiteImage {
  src: string;
  width: number;
  height: number;
  source: 'capture' | 'upload';
}

export interface SiteShot {
  id: string;
  /** The library's name for it, "builder.png". */
  file: string;
  alt: string;
  /** The browser-bar text in the frame above it on the Features page. */
  url: string;
  desktop: SiteImage;
  /** The 780×960 crop served under 640px; null shrinks the desktop shot instead. */
  phone: SiteImage | null;
}

/** The published document and the screenshot library, as the public pages read them. */
export interface SiteContent {
  doc: SiteDoc;
  shots: Record<string, SiteShot>;
}

export const FEATURE_ROW_IDS = [
  'builder', 'ai', 'logic', 'responses', 'workflow', 'documents', 'access', 'invitations', 'payments',
] as const;

/**
 * The accent swatches, in the design's order. The colours are tokens
 * (colors.css `--site-accent-*`; indigo is the product's own --accent), and
 * site.css's "The look" re-points the site's accent ramp to each.
 */
export const ACCENTS: { key: SiteAccent; label: string }[] = [
  { key: 'indigo', label: 'Indigo · current' },
  { key: 'green', label: 'Green' },
  { key: 'orange', label: 'Orange' },
  { key: 'ink', label: 'Ink' },
];

// ---------------------------------------------------------------------------
// The launch copy: the seed 078 wrote, and the fallback. Word for word what
// HomePage and FeaturesPage hard-coded before Site content existed.
// ---------------------------------------------------------------------------

export const DEFAULT_SITE: SiteDoc = {
  look: { accent: 'indigo', font: 'sans', radius: 'round', favicon: null },
  home: {
    eyebrow: 'New · every screen works on a phone',
    eyebrowLink: '/community/releases',
    h1: 'Forms that do the work after someone hits submit',
    lede: 'Build a form from a sentence. Send each answer through branches, reminders, signatures and payments. '
      + 'Review everything in one inbox, with no automation bill in the middle.',
    // Only true of the Free plan — paid trials take a card — so it says so (redesign playbook §4).
    cta1: 'Start free · no card on the Free plan',
    cta2: 'See pricing',
    hero: 'builder',
    pillars: [
      {
        k: '01 · BUILD',
        title: 'Describe it, then shape it',
        body: 'AI drafts, pages, repeating groups, logic that reads like a sentence, and your own PDF.',
      },
      {
        k: '02 · RUN',
        title: 'Draw what happens next',
        // 18 = the delivery catalogue (15 integrations + Monday, webhook, email);
        // the follow-up form is a workflow step, not a destination.
        body: 'Branch, remind, hand off to a second form, take payment and deliver to 18 tools.',
      },
      {
        k: '03 · REVIEW',
        title: 'An inbox, not an export',
        body: 'Assign, set status and edit with full history. SSO and an audit log for when IT asks.',
      },
    ],
  },
  features: {
    h1: 'Everything a serious form needs',
    sub: 'A working tour of the platform, in the order a team meets it: build a form — or have AI draft it — '
      + 'collect answers and signatures, invite the people who should fill it in, and draw the workflow that '
      + 'carries every submission where the work happens.',
    rows: [
      {
        id: 'builder', short: 'Builder', title: 'The form builder', shot: 'builder',
        lede: 'Compose forms from a full field palette — text, email, phone, number, date, dropdowns, checkboxes, '
          + 'ratings, file uploads and more — organised into sections and multi-page forms with progress.',
        points: [
          'Composite Name and Address fields, including a shipping variant with Company, Attention, and Phone',
          'Repeating widgets for line items, with nested children (items inside orders, parts inside items)',
          'Per-field validation with clear inline errors',
          'Starter templates so a new form is never a blank page',
          'Duplicate a form with its workflow or without it; edits wait in a draft until you publish them',
        ],
      },
      {
        id: 'ai', short: 'AI drafts', title: 'Describe it, and AI drafts it', shot: 'ai',
        lede: 'Type what the form is for — “a catering enquiry form: contact details, event date, guest count, '
          + 'dietary requirements, budget range” — and the AI builder hands you a structured draft in the ordinary '
          + 'builder, ready to review, tweak, and publish.',
        points: [
          'Drafts use the full palette: sections, composite fields, signatures, ratings, matrices',
          'Always lands as a draft — nothing goes live without a person publishing it',
          'Included on every paid plan, with a monthly allowance that scales by plan',
          'No API keys to bring or manage — it works out of the box',
        ],
      },
      {
        id: 'logic', short: 'Logic', title: 'Conditional logic', shot: 'logic',
        lede: "Rules read the way the form does. The target picker mirrors your form's structure — sections and "
          + 'cards head their own groups — so a rule says what it does at a glance.',
        points: [
          'Show, hide, or require individual fields, whole sections, or cards all in one rule',
          'Target composite sub-fields: city, state, last name, company',
          'Reveal hidden fields only when a rule decides they matter',
          'Per-page logic on multi-page forms',
          'Calculated values stored with the submission',
        ],
      },
      {
        id: 'responses', short: 'Responses', title: 'Responses, kept honest', shot: 'submissions',
        lede: 'Every form has an inbox: filter and sort columns, save views, open a response beside the list '
          + 'without losing your place, and edit values with a full revision history.',
        points: [
          'Saved views with per-user column layouts',
          'Attachments stored and served with each submission',
          'Edits tracked as revisions — who changed what, when',
          'Notification emails when submissions arrive',
          'Honeypot anti-spam on every public form',
        ],
      },
      {
        id: 'workflow', short: 'Workflow', title: 'A workflow you can see', shot: 'workflow',
        lede: 'What happens after submit is drawn on a canvas, not buried in settings — and it can replace the '
          + 'Zapier or n8n subscription you were paying just to move form data around. Branch on an answer, hand '
          + 'off to a second form, remind whoever hasn’t finished, and deliver each path to a different tool.',
        points: [
          'Branch and rejoin on any answer; every step shows its run order',
          'Follow-up links chain a second form that continues the first — signatures, approvals, part two',
          'Automatic reminders on the days you choose, re-sent into the same email or helpdesk ticket',
          'Per-path endings: a message, a redirect, or straight into the next form',
          '“Try it” dry-runs the whole flow against a sample, sending nothing; every real run can be replayed step by step',
        ],
      },
      {
        id: 'documents', short: 'Documents & signatures', title: 'Signatures and documents', shot: 'document',
        lede: 'A draw-or-type signature field that would survive a dispute: it records the exact terms shown to '
          + 'that person — answers filled in — with a timestamp, IP and a hash of the text, and stamps the result '
          + 'onto a real document.',
        points: [
          'Draw or type; both produce the same signed record',
          'Consent text and contract terms frozen per signature — a later edit cannot change what was agreed',
          'Smart PDF: upload your own template and answers land on your paperwork, not ours',
          'LOA starter templates: provider starts, customer signs, reminders chase the unsigned',
        ],
      },
      {
        id: 'access', short: 'Access & SSO', title: 'Access control and single sign-on', shot: 'access',
        lede: 'Decide who can open each form: anyone with the link, people signed in through your identity '
          + 'provider, or someone holding a short-lived magic link.',
        points: [
          'Per-account and per-form OIDC providers, configured by you',
          'SAML support for enterprise identity providers',
          'Password-protected forms for the simple cases',
          'Short-lived links that expire on a timer',
          'Submitter identity recorded with the submission',
        ],
      },
      {
        id: 'invitations', short: 'Invitations', title: 'Invite a list, see who finished', shot: 'invitations',
        lede: 'Surveys and sign-ups start with a list, not a link on a website. Upload a CSV — or paste the list — '
          + 'of names and addresses and every person gets an email with their own private link to the form; then '
          + 'watch the batch fill in.',
        points: [
          'Per-person status: queued, sent, opened, completed — the follow-up call goes to the right people',
          'Remind whoever has not finished, once or on the days you choose',
          '{{name}} personalises the subject and message from your list',
          'Test the email in your own inbox before anything goes to the list',
          'Sends drip out at a spam-safe pace automatically — big lists just keep draining',
        ],
      },
      {
        id: 'payments', short: 'Payments & delivery', title: 'Payments and delivery', shot: 'connections',
        lede: 'A form is rarely the end of the story. Charge on submit, then deliver the data to the tools your '
          + 'team already lives in — eighteen destinations, no middleman.',
        points: [
          'Payment fields backed by Stripe Connect — funds settle to your own Stripe account, and the payment’s '
            + 'status is kept with the submission',
          'Slack, Teams, Discord; Google Sheets, Airtable, Notion; Salesforce, HubSpot; Jira, Trello, Asana; '
            + 'Zendesk, Freshdesk; monday.com boards with full column mapping',
          'HMAC-signed webhooks, plus Zapier and Make when you want them',
          'A delivery log per submission, with one-click redelivery',
        ],
      },
    ],
  },
};

const capture = (file: string): SiteImage => ({ src: `/marketing/${file}`, width: 2880, height: 1800, source: 'capture' });
const phoneCapture = (file: string): SiteImage => ({ src: `/marketing/${file}`, width: 780, height: 960, source: 'capture' });

/** The nine captures (scripts/marketing-shots*.mjs), in the library's order. */
export const DEFAULT_SHOTS: SiteShot[] = [
  ['builder', 'form-magic.com/admin — Form builder', "Editing a form's sections and fields in the builder", true],
  ['ai', 'form-magic.com/admin — New form', 'The New form dialog on “Describe it”, ready for a description for AI to draft', false],
  ['logic', 'form-magic.com/admin — Logic', 'The logic editor with rules written as sentences beside the form', true],
  ['submissions', 'form-magic.com/admin — Responses', 'The responses list with filters and a response open beside it', true],
  ['workflow', 'form-magic.com/admin — Workflow',
    'The workflow canvas: a branch on the ticket type, an email to the events team, and an ending for each path', true],
  ['document', 'form-magic.com/admin — Document',
    'A confirmation PDF composed from a heading, a paragraph and the answers, previewed with sample answers', true],
  ['access', 'form-magic.com/admin — Sign-in & access', "Configuring how an account's team signs in", true],
  ['invitations', 'form-magic.com/admin — Invitations', 'An invitation batch showing who opened and who finished the form', true],
  ['connections', 'form-magic.com/admin — Workflow',
    'An email delivery step open beside the workflow: who it goes to and what it says', true],
].map(([id, url, alt, hasPhone]) => ({
  id: id as string,
  file: `${id}.png`,
  alt: alt as string,
  url: url as string,
  desktop: capture(`${id}.png`),
  phone: hasPhone ? phoneCapture(`${id}-m.png`) : null,
}));

export const shotsById = (list: SiteShot[]): Record<string, SiteShot> =>
  Object.fromEntries(list.map((s) => [s.id, s]));

export const DEFAULT_CONTENT: SiteContent = { doc: DEFAULT_SITE, shots: shotsById(DEFAULT_SHOTS) };

/** Pages that show a shot the Site tab does not choose: the sign-in and sign-up asides. */
export const FIXED_USES: Record<string, string[]> = { builder: ['Sign-in page'], submissions: ['Sign-up page'] };

/** A shot by id — the library's, else the fallback's, else the builder: a page never draws a hole. */
export function shotOf(content: SiteContent, id: string): SiteShot {
  return content.shots[id] || DEFAULT_CONTENT.shots[id] || content.shots.builder || DEFAULT_CONTENT.shots.builder;
}

/** Every page a shot appears on, in the words the console uses ("Home · hero", "Features · Builder"). */
export function usedOn(doc: SiteDoc, id: string): string[] {
  const out: string[] = [];
  if (doc.home.hero === id) out.push('Home · hero');
  for (const r of doc.features.rows) if (r.shot === id) out.push(`Features · ${r.short || r.title}`);
  return [...out, ...(FIXED_USES[id] || [])];
}

// ---------------------------------------------------------------------------
// Reading a document defensively, and comparing two.
// ---------------------------------------------------------------------------

const isStr = (v: unknown): v is string => typeof v === 'string';
const oneOf = <T extends string>(v: unknown, ok: readonly T[], fallback: T): T =>
  (typeof v === 'string' && (ok as readonly string[]).includes(v) ? (v as T) : fallback);
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : {});

/**
 * A document from the wire, every leaf checked against the fallback's shape:
 * a missing or wrong-typed value is the fallback's. What an older or newer
 * server sends can never leave a page without a headline.
 */
export function resolveDoc(raw: unknown, fallback: SiteDoc = DEFAULT_SITE): SiteDoc {
  const d = obj(raw);
  const look = obj(d.look);
  const home = obj(d.home);
  const features = obj(d.features);
  const text = (src: Record<string, unknown>, key: string, fb: string) => (isStr(src[key]) ? src[key] as string : fb);
  const pillars = Array.isArray(home.pillars) ? home.pillars : [];
  const rows = Array.isArray(features.rows) ? features.rows.map(obj) : [];
  return {
    look: {
      accent: oneOf(look.accent, ['indigo', 'green', 'orange', 'ink'] as const, fallback.look.accent),
      font: oneOf(look.font, ['sans', 'serif'] as const, fallback.look.font),
      radius: oneOf(look.radius, ['round', 'square'] as const, fallback.look.radius),
      favicon: isStr(look.favicon) && look.favicon ? look.favicon : look.favicon === null ? null : fallback.look.favicon,
    },
    home: {
      eyebrow: text(home, 'eyebrow', fallback.home.eyebrow),
      eyebrowLink: text(home, 'eyebrowLink', fallback.home.eyebrowLink),
      h1: text(home, 'h1', fallback.home.h1),
      lede: text(home, 'lede', fallback.home.lede),
      cta1: text(home, 'cta1', fallback.home.cta1),
      cta2: text(home, 'cta2', fallback.home.cta2),
      hero: text(home, 'hero', fallback.home.hero),
      pillars: fallback.home.pillars.map((fb, i) => {
        const p = obj(pillars[i]);
        return { k: text(p, 'k', fb.k), title: text(p, 'title', fb.title), body: text(p, 'body', fb.body) };
      }),
    },
    features: {
      h1: text(features, 'h1', fallback.features.h1),
      sub: text(features, 'sub', fallback.features.sub),
      rows: fallback.features.rows.map((fb) => {
        const r = rows.find((x) => x.id === fb.id) || {};
        return {
          id: fb.id,
          short: text(r, 'short', fb.short),
          title: text(r, 'title', fb.title),
          lede: text(r, 'lede', fb.lede),
          points: Array.isArray(r.points) && r.points.every(isStr) ? [...r.points as string[]] : [...fb.points],
          shot: text(r, 'shot', fb.shot),
        };
      }),
    },
  };
}

/** A document flattened to its leaves: "home.pillars.1.title" → "Draw what happens next". */
function leaves(v: unknown, path = '', out: Record<string, unknown> = {}): Record<string, unknown> {
  if (v && typeof v === 'object') {
    for (const [k, child] of Object.entries(v as Record<string, unknown>)) leaves(child, path ? `${path}.${k}` : k, out);
  } else {
    out[path] = v;
  }
  return out;
}

/**
 * Every leaf that differs between two documents — the publish bar's count
 * ("2 unpublished changes") and, on the server, the audit entry's list. A
 * bullet added to a row is one change, as is each one edited.
 */
export function changedPaths(a: SiteDoc, b: SiteDoc): string[] {
  const A = leaves(a);
  const B = leaves(b);
  return [...new Set([...Object.keys(A), ...Object.keys(B)])].filter((k) => A[k] !== B[k]);
}

/** A link inside the site ("/community/releases", "/features#access") — never another origin. */
export const isSiteRelativePath = (s: string): boolean => /^\/(?![/\\])[^\s]*$/.test(s);
